import {
    Center,
    HStack,
    Pressable,
    Spinner,
    Stack,
    Text,
    View,
    ZStack,
} from 'native-base';
import React, { useEffect, useMemo, useRef } from 'react';
import { useState } from 'react';
import { Modal } from 'react-native';
import useMeasure from 'react-use-measure';
import { useCookies } from 'react-cookie';
import { useSetIsLoggedIn } from '../hooks/useIsLoggedIn';
import { useShopInfo } from '../queries/shop';
import AnimatedVStack from './AnimatedVStack';
import Carousel from './Carousel';
import { CloseButton } from './CloseButton';
import { SkeletonImage } from './SkeletonImage';
import { Swiper, SwiperSlide, useSwiper } from 'swiper/react';
import { Pagination } from 'swiper';

import 'swiper/css';
import 'swiper/css/pagination';
import '../style/PopUpSwiper.css'
import ActionLink from './ActionLink';
import { useSessionStorage } from 'usehooks-ts';
import { animated, useSpring } from 'react-spring';
import { useInView } from 'react-intersection-observer';
import _ from 'lodash';

const sid = 'adHistory';

const useAdHistory = () => {
    const [adHistory, setAdHistory] = useSessionStorage(sid, {});

    const addAdHistoryRecord = adId => {
        const list = Array.isArray(adId) ? adId : [adId];
        setAdHistory(prev => {
            return {
                ...prev,
                ...list.reduce(
                    (prev, curr) => ({ ...prev, [curr]: Date.now() }),
                    {},
                ),
            };
        });
    };

    return [adHistory, addAdHistoryRecord];
};

const useAdFreqHist = () => {
    const [cookies, setCookie] = useCookies([sid]);
    const localHistory = JSON.parse(localStorage.getItem(sid)) ?? {};
    // const cookiesHistory = useMemo(() => (cookies[sid]? cookies[sid]: {}), []);
    const cookiesHistory = cookies[sid]? cookies[sid]: {};
    const adFreqHist = useMemo(() => ({...localHistory,...cookiesHistory}),[]);

    // type of freq: forever_once, once, forever
    // forever_once, localStorage
    const addAdFreqHist = (adId, frequency) => {
        const idList = Array.isArray(adId) ? adId: [adId];
        idList.forEach((id) => {
            if (frequency === 'forever_once'){
                localHistory[id] = Date.now();
            }
            // once, session cookies
            if (frequency === 'once'){
                cookiesHistory[id] = Date.now();
            }
        });
        localStorage.setItem(sid, JSON.stringify(localHistory));
        setCookie(sid, JSON.stringify(cookiesHistory),  {path: "/" });
    }

    return [adFreqHist, addAdFreqHist];
};

const TopMessage = ({ topMsg }) => {
    const { adId, title, allowClose, action, actionTarget, frequency } = topMsg;
    const [show, setShow] = useState(true);

    const [ref, { height: containerHeight }] = useMeasure();

    const style = useSpring({
        height: containerHeight && show ? containerHeight : 0,
    });

    const showCloseButton = allowClose === true;

    const [, addAdFreqHistory] = useAdFreqHist();

    useEffect(() => {
        addAdFreqHistory(adId, frequency);
    }, []);


    return (
        <AnimatedVStack bg={'lightText'} style={style}>
            <View ref={ref}>
                <HStack
                    margin={'2.5'}
                    paddingLeft={'6'}
                    paddingRight={showCloseButton ? 0 : '6'}>
                    
                    {show && (
                        <Center flex={1}>
                            <ActionLink action={action} actionTarget={actionTarget}>
                                <Text color={'white'} textAlign={'center'}>
                                    {title}
                                </Text>
                            </ActionLink>
                        </Center>
                    )}
                    
                    {show && showCloseButton && (
                        <CloseButton
                            onPress={() => {
                                setShow(false);
                            }}
                            color={'white'}
                        />
                    )}
                </HStack>
            </View>
        </AnimatedVStack>
    );
};

export const Popup = ({ popupAd = [] }) => {
    const [open, setOpen] = useState(true);
    const [loading, setLoading] = useState(true);
    const [sizes, setSizes] = useState({});
    const [loadedAdId, setLoadedAdId ]= useState([]);
    const [, saveAdFreqHist] = useAdFreqHist();

    // image onload will not trigger if image is already loaded, so remove duplicate here
    const prelaodImageList = useMemo(() => _.uniq(popupAd.map(({image}) => image)), [popupAd]);

    const [ref, { width: containerWidth = 0, height: containerHeight = 0 }] =
        useMeasure();

    useEffect(() => {
        if (
            Object.keys(sizes).length === prelaodImageList.length &&
            containerWidth > 0
        ) {
            setLoading(false);
        }
    }, [containerWidth, sizes]);

    useEffect(() => {
        if(loadedAdId && loadedAdId.length === popupAd.length){
            const frequency = 'once';
            saveAdFreqHist(loadedAdId, frequency);
        }
    }, [loadedAdId]);


    return (
        <Modal visible={open} transparent={true}>
            <>
                <Pressable
                    position={'absolute'}
                    size={'full'}
                    background={'black'}
                    opacity={0.7}
                    onPress={() => {
                        setOpen(false);
                    }}
                />
                <Center size={'full'} marginTop={'8'}>
                    <View ref={ref} width={'full'} height={'80%'}>
                        {loading ? (
                            <>
                                <Spinner size={'lg'} color={'white'} />
                                <HStack height={'full'}>
                                    {prelaodImageList.map((image, index) => (
                                        <img
                                            key={index}
                                            style={{
                                                opacity: 0,
                                            }}
                                            src={image}
                                            onLoad={e => {
                                                setSizes({
                                                    ...sizes,
                                                    [image]: {
                                                        width: e.target
                                                            .naturalWidth,
                                                        height: e.target
                                                            .naturalHeight,
                                                    },
                                                });
                                            }}
                                        />
                                    ))}
                                </HStack>
                            </>
                        ) : (
                            <Swiper
                                className='popup'
                                slidesPerView={'auto'}
                                centeredSlides={true}
                                pagination={true}
                                modules={[Pagination]}
                                spaceBetween={30}
                                style={{
                                    width: '100%',
                                    height: '100%',
                                }}>
                                {popupAd.map(
                                    (
                                        { adId, image, action, actionTarget },
                                        index,
                                    ) => {
                                        const width = sizes[image]
                                            ? sizes[image].width
                                            : 0;
                                        const height = sizes[image]
                                            ? sizes[image].height
                                            : 0;

                                        const width_ratio = width/containerWidth;
                                        const height_ratio = height/containerHeight;

                                        let slideWidth = 'auto';
                                        let slideHeight = 'auto';
                                        
                                        if ( width_ratio > 1 || height_ratio > 1 ) {
                                            if ( width_ratio > height_ratio ) {
                                                slideWidth = '100%';
                                            }
                                            
                                            if ( width_ratio <= height_ratio ) {
                                                slideHeight = '100%';
                                            }
                                        }

                                        // const ratio = width / height;
                                        // const containerRatio =
                                        //     containerWidth / containerHeight;

                                        // let slideWidth = '100%';
                                        // let slideHeight = '100%';

                                        // if (containerRatio > 1) {
                                        //     slideWidth = `${
                                        //         ((ratio * containerHeight) /
                                        //             containerWidth) *
                                        //         100
                                        //     }%`;
                                        // }

                                        // if ( containerRatio <= 1) {
                                        //     slideHeight = `${
                                        //         (((height / width) *
                                        //             containerWidth) /
                                        //             containerHeight) *
                                        //         100
                                        //     }%`;
                                        // }

                                        return (
                                            <SwiperSlide
                                                key={index}
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                }}>
                                                <AdSlideContent
                                                    adId={adId}
                                                    slideHeight={slideHeight}
                                                    slideWidth={slideWidth}
                                                    action={action}
                                                    actionTarget={actionTarget}
                                                    image={image}
                                                    onLoad={() => {
                                                        setLoadedAdId(prev => [...prev, adId]);
                                                    }}
                                                />
                                            </SwiperSlide>
                                        );
                                    },
                                )}
                            </Swiper>
                        )}
                    </View>
                </Center>
                <CloseButton
                    isWhiteIcon={true}
                    position={'absolute'}
                    top={30}
                    right={30}
                    size={'12'}
                    onPress={() => {
                        setOpen(false);
                    }}
                />
            </>
        </Modal>
    );
};

const AdSlideContent = ({
    adId,
    slideHeight,
    slideWidth,
    action,
    actionTarget,
    image,
    onLoad,
    onShow,
}) => {

    return (
        <Center height={'100%'}>
            <ActionLink 
                action={action} 
                actionTarget={actionTarget}
                style={{
                    height: slideHeight,
                    width: slideWidth,
                }}
            >
                <FadeImage
                    style={{
                        height: slideHeight,
                        width: slideWidth,
                        borderRadius: 30,
                    }}
                    src={image}
                    onLoad={onLoad}
                />
            </ActionLink>
        </Center>
    );
};

const FadeImage = ({ onShow, ...props }) => {
    const { ref, inView, entry } = useInView({
        threshold: 0,
        triggerOnce: true,
    });

    const fadeInOut = useSpring({
        opacity: inView ? 1 : 0,
    });

    useEffect(() => {
        if (inView && typeof onShow === 'function') {
            onShow();
        }
    }, [inView]);

    return (
        <animated.img
            {...props}
            ref={ref}
            style={{
                ...props.style,
                ...fadeInOut,
            }}
        />
    );
};

export const AdComponent = () => {
    const { data: shopInfo } = useShopInfo();
    const { ads = {} } = shopInfo;
    const { popupAd = [], topMsg = {} } = ads;

    const [adFreqHist, saveFreqHistory] = useAdFreqHist();

    const freqHistAdIdList = Object.keys(adFreqHist);

    const filteredPopupAd = popupAd.filter(
        ad =>!freqHistAdIdList.includes(`${ad.adId}`));

    const topMsgList = Array.isArray(topMsg) ? topMsg : [topMsg];

    const filteredTopMsgList = topMsgList
        .filter(ad => ad)
        .filter(ad =>!freqHistAdIdList.includes(`${ad.adId}`));

    return (
        <>
            {filteredTopMsgList.length > 0 &&
                filteredTopMsgList.map((topMsg, index) => (
                    <TopMessage key={index} topMsg={topMsg} />
                ))}
            {filteredPopupAd.length > 0 && <Popup popupAd={filteredPopupAd} />}
        </>
    );
};
